import { PlanungModules } from '@bp/pim-auth-constants';
import { EmptyState, LockIcon, Modal, Tooltip } from '@bp/ui-components';
import { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './LicenseHint.module.scss';

/**
 * Buchungshinweis für nicht gebuchte Module (Frontend #63, Plan im Backend:
 * docs/plans/2026-11-03-module-licensing-plan.md, Entscheidung 10). Freundlich, je Stelle passend, ohne Shop oder Link.
 */
export type LicenseHintKind =
  | { kind: 'module'; module: string }
  | { kind: 'trial'; module: string }
  | { kind: 'solver' }
  | { kind: 'activate' }
  | { kind: 'export'; module: PlanungModules.teachingLoad | PlanungModules.timetable }
  | { kind: 'teachingBlocks' }
  | { kind: 'teachingLoad' }
  | { kind: 'watermark' }
  | { kind: 'absences' }
  | { kind: 'publish' }
  | { kind: 'app' };

export const moduleName = (t: TFunction, module: string): string => {
  switch (module) {
    case PlanungModules.teachingLoad:
      return t('license.modules.teachingLoad');
    case PlanungModules.timetable:
      return t('license.modules.timetable');
    case PlanungModules.solver:
      return t('license.modules.solver');
    case PlanungModules.teachingBlocks:
      return t('license.modules.teachingBlocks');
    case PlanungModules.substitution:
      return t('license.modules.substitution');
    case PlanungModules.app:
      return t('license.modules.app');
    default:
      return module;
  }
};

export const licenseHintText = (t: TFunction, hint: LicenseHintKind): string => {
  switch (hint.kind) {
    case 'module':
      return t('license.hint.module', { module: moduleName(t, hint.module) });
    case 'trial':
      return t('license.hint.trial', { module: moduleName(t, hint.module) });
    case 'solver':
      return t('license.hint.solver');
    case 'activate':
      return t('license.hint.activate');
    case 'export':
      return t('license.hint.export', { module: moduleName(t, hint.module) });
    case 'teachingBlocks':
      return t('license.hint.teachingBlocks');
    case 'teachingLoad':
      return t('license.hint.teachingLoad');
    case 'watermark':
      return t('license.hint.watermark');
    case 'absences':
      return t('license.hint.absences');
    case 'publish':
      return t('license.hint.publish');
    case 'app':
      return t('license.hint.app');
  }
};

/**
 * Hinweis zu einem Fehler `LICENSE_REQUIRED` vom Server. `VERTRETUNG` kommt dort fast nur von der sechsten Person mit
 * Abwesenheit (Veröffentlichen prüft das Frontend vorher), deshalb dieser Text.
 */
export const licenseHintForModule = (module: string): LicenseHintKind =>
  module === PlanungModules.substitution ? { kind: 'absences' } : { kind: 'module', module };

let openLicenseHint: ((hint: LicenseHintKind) => void) | null = null;

/**
 * Lehnt der Server eine REST-Anfrage ab, weil das Modul nicht gebucht ist (403, `code: LICENSE_REQUIRED`), öffnet das
 * den Buchungshinweis und liefert `true`; der Aufrufer zeigt dann keinen eigenen Fehler.
 */
export async function showLicenseHintIfRequired(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  const body = (await response
    .clone()
    .json()
    .catch(() => null)) as { code?: string; module?: string } | null;
  if (body?.code !== 'LICENSE_REQUIRED') return false;
  showLicenseHint(licenseHintForModule(body.module ?? ''));
  return true;
}

/** Öffnet den Buchungshinweis als Modal, auch außerhalb von React (Fehlerbehandlung von urql und apiHelper). */
export function showLicenseHint(hint: LicenseHintKind) {
  if (openLicenseHint) {
    openLicenseHint(hint);
  } else {
    console.warn('LicenseHintHost is not mounted.');
  }
}

/** Einmal in der App eingehängt; zeigt den Buchungshinweis, den {@link showLicenseHint} anfordert. */
export const LicenseHintHost = () => {
  const { t } = useTranslation();
  const [hint, setHint] = useState<LicenseHintKind | null>(null);
  openLicenseHint = setHint;

  if (!hint) return null;
  return (
    <Modal
      parent={document.fullscreenElement ? document.getElementById('timetable-grid') : null}
      isOpen
      width='s'
      onRequestClose={() => setHint(null)}
      title={t('license.title')}
      hideFooter
    >
      <p className={styles.text}>{licenseHintText(t, hint)}</p>
    </Modal>
  );
};

/**
 * Schloss an einer gesperrten Stelle (Aktion, Kachel, Tabellenzelle); der Tooltip nennt den Hinweis, ein Klick öffnet
 * ihn.
 */
export const LicenseLock = ({ hint }: { hint: LicenseHintKind }) => {
  const { t } = useTranslation();
  return (
    <Tooltip content={<span className={styles['tooltip-text']}>{licenseHintText(t, hint)}</span>}>
      <button
        type='button'
        className={styles['lock-button']}
        aria-label={t('license.title')}
        onClick={(event) => {
          event.stopPropagation();
          showLicenseHint(hint);
        }}
      >
        <LockIcon className='small' />
      </button>
    </Tooltip>
  );
};

/** Ersetzt den Inhalt einer Seite oder eines Reiters, dessen Modul nicht gebucht ist (z. B. Epochen). */
export const LicenseEmptyState = ({ hint }: { hint: LicenseHintKind }) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      title={t('license.title')}
      subtitle={licenseHintText(t, hint)}
      icon={<LockIcon />}
      iconColor='var(--color-grey)'
      padding='l'
      maxWidth='480px'
    />
  );
};
